import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Sparkles, AlertTriangle, Clock, CreditCard, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TestInterview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [createdLink, setCreatedLink] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, jobsRes, candidatesRes] = await Promise.all([
        fetch(`${API_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${API_URL}/api/jobs`, { credentials: 'include' }),
        fetch(`${API_URL}/api/candidates`, { credentials: 'include' })
      ]);

      if (userRes.ok) setUser(await userRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
      if (candidatesRes.ok) setCandidates(await candidatesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const filteredCandidates = selectedJob 
    ? candidates.filter(c => c.job_id === selectedJob)
    : [];

  const createTestInterview = async () => {
    if (!selectedJob || !selectedCandidate) {
      toast.error('Veuillez sélectionner un poste et un candidat');
      return;
    }

    if ((user?.interview_credits || 0) < 1) {
      toast.error('Crédits insuffisants');
      navigate('/billing');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/interview-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_id: selectedJob,
          candidate_ids: [selectedCandidate],
          link_type: 'individual',
          interview_language: 'fr',
          is_bilingual: false,
          send_email: false // Don't send email for test
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création');
      }

      const data = await response.json();
      setCreatedLink(data);
      toast.success('Lien d\'entretien créé !');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startInterview = () => {
    if (createdLink?.interview_url) {
      // Open in new tab
      window.open(createdLink.interview_url, '_blank');
    }
  };

  const credits = user?.interview_credits || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-lime-100 flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-lime-600" />
            </div>
            <CardTitle className="text-2xl">{t('testInterview.title', 'Test Entretien IA')}</CardTitle>
            <CardDescription className="text-base">
              {t('testInterview.description', 'Créez un vrai entretien de 3 minutes pour tester l\'expérience candidat')}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6 pt-4">
            {/* Credits Warning */}
            <div className={`flex items-center gap-3 p-4 rounded-lg ${credits > 0 ? 'bg-lime-50 border border-lime-200' : 'bg-red-50 border border-red-200'}`}>
              <CreditCard className={`h-5 w-5 ${credits > 0 ? 'text-lime-600' : 'text-red-600'}`} />
              <div className="flex-1">
                <p className={`font-medium ${credits > 0 ? 'text-lime-700' : 'text-red-700'}`}>
                  {credits} {t('dashboard.credits', 'crédits')} {t('common.available', 'disponibles')}
                </p>
                <p className="text-sm text-zinc-500">
                  {t('testInterview.creditNote', 'Ce test consommera 1 crédit')}
                </p>
              </div>
              {credits < 1 && (
                <Button size="sm" onClick={() => navigate('/billing')}>
                  {t('billing.buyCredits', 'Acheter')}
                </Button>
              )}
            </div>

            {/* Duration Info */}
            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-700">{t('testInterview.duration', 'Durée: ~3 minutes')}</p>
                <p className="text-sm text-blue-600">
                  {t('testInterview.durationNote', 'Entretien court pour validation rapide')}
                </p>
              </div>
            </div>

            {!createdLink ? (
              <>
                {/* Job Selection */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-zinc-700">
                    {t('interviewLinks.form.selectJob', 'Sélectionner un poste')} *
                  </label>
                  <Select value={selectedJob} onValueChange={(v) => { setSelectedJob(v); setSelectedCandidate(''); }}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('interviewLinks.form.selectJob')} />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map(job => (
                        <SelectItem key={job.job_id} value={job.job_id}>
                          {job.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Candidate Selection */}
                {selectedJob && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-zinc-700">
                      {t('interviewLinks.form.selectCandidates', 'Sélectionner un candidat')} *
                    </label>
                    <Select value={selectedCandidate} onValueChange={setSelectedCandidate}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('interviewLinks.form.selectCandidates')} />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredCandidates.length > 0 ? (
                          filteredCandidates.map(candidate => (
                            <SelectItem key={candidate.candidate_id} value={candidate.candidate_id}>
                              {candidate.name} - {candidate.email}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>
                            {t('common.noCandidates', 'Aucun candidat pour ce poste')}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  onClick={createTestInterview} 
                  disabled={isLoading || credits < 1 || !selectedJob || !selectedCandidate}
                  className="w-full bg-zinc-900 hover:bg-zinc-800 h-12 text-base"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      {t('common.loading')}
                    </div>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 mr-2" />
                      {t('testInterview.createLink', 'Créer le lien d\'entretien')}
                    </>
                  )}
                </Button>
              </>
            ) : (
              /* Link Created */
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">{t('interviewLinks.linkCreated', 'Lien créé !')}</p>
                    <p className="text-sm text-green-600">
                      {t('testInterview.linkReady', 'Votre entretien test est prêt')}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-zinc-100 rounded-lg">
                  <p className="text-sm text-zinc-500 mb-2">{t('testInterview.linkUrl', 'Lien d\'entretien:')}</p>
                  <code className="text-xs break-all text-zinc-700">{createdLink.interview_url}</code>
                </div>

                <div className="flex gap-3">
                  <Button 
                    onClick={startInterview}
                    className="flex-1 bg-lime-500 hover:bg-lime-600 text-zinc-900 h-12"
                  >
                    <Sparkles className="h-5 w-5 mr-2" />
                    {t('testInterview.startNow', 'Démarrer l\'entretien')}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(createdLink.interview_url);
                      toast.success(t('interviewLinks.linkCopied'));
                    }}
                    className="h-12"
                  >
                    {t('common.copy', 'Copier')}
                  </Button>
                </div>

                <p className="text-center text-zinc-500 text-sm">
                  {t('testInterview.reportNote', 'Le rapport sera envoyé dans votre dashboard après l\'entretien')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
